import React, { useState, useRef } from 'react';
import { Card, Col, Tag, Row, Space, Typography, Divider, Button, Tooltip } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatWholeNumberNaN } from '../../../../../core/utils/campaigns';
import { CodeBlock } from 'react-code-blocks';
import GenTagInstructionsModal from './GenTagInstructionsModal';
import GeneratedImagePixelTagModal from './GeneratedImagePixelTagModal';
import GeneratedTagsCardStyles from '../../styles/GeneratedTagsCardStyles';

const { Text } = Typography;

const formatJavaScriptCode = (code) => {
  if (!code) return '';

  // Basic JavaScript formatting with proper indentation
  let formatted = code
    // Remove extra whitespace and normalize line breaks
    .trim()
    // Add proper spacing around operators and brackets
    .replace(/\s*{\s*/g, ' {\n  ')
    .replace(/\s*}\s*/g, '\n}')
    .replace(/;\s*/g, ';\n  ')
    .replace(/,\s*/g, ',\n    ')
    // Fix function calls and object properties
    .replace(/(\w+)\s*\(/g, '$1(')
    .replace(/:\s*/g, ': ')
    // Clean up extra indentation
    .replace(/\n\s+\n/g, '\n\n')
    // Normalize indentation
    .split('\n')
    .map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return '';

      // Calculate indentation level
      let indentLevel = 0;
      if (trimmedLine.includes('{') && !trimmedLine.includes('}')) indentLevel = 0;
      else if (trimmedLine.includes('}')) indentLevel = -1;
      else if (index > 0) indentLevel = 1;

      const indent = '  '.repeat(Math.max(0, indentLevel));
      return indent + trimmedLine;
    })
    .join('\n');

  return formatted;
};

const typePicker = (date, value) => {
  let color = 'success';
  if (!date) {
    color = 'secondary';
  } else if (date && value < 1) {
    color = 'danger';
  }

  return color;
};

const colorPicker = (date, value) => {
  let color = 'success';
  if (!date) {
    color = 'default';
  } else if (date && value < 1) {
    color = 'error';
  }

  return color;
};

const GeneratedTagsCard = ({ generatedTag, type, details }) => {
  const { pageViews, transactions, sessions, impressions, basketItems, date, tag, cart } =
    generatedTag;

  const { appId } = details;
  const generatedTagValue = tag ? tag.generatedTag : '';

  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  const handleCopy = () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    navigator.clipboard.writeText(generatedTagValue);
    setIsCopied(true);

    copyTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      copyTimeoutRef.current = null;
    }, 3000);
  };

  const tagCards = () => {
    if (type === 'measurement') {
      return (
        <>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              size="small"
              style={{
                ...GeneratedTagsCardStyles.metricCard,
                border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, pageViews, colorPicker)}`,
                backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                  date,
                  pageViews,
                  colorPicker
                ),
              }}
              bodyStyle={GeneratedTagsCardStyles.metricCardBody}
            >
              <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                  Page Views
                </Text>
                <Text
                  style={{
                    ...GeneratedTagsCardStyles.metricValue,
                    color: GeneratedTagsCardStyles.getValueTextColor(date, pageViews, colorPicker),
                  }}
                >
                  {formatWholeNumberNaN(pageViews)}
                </Text>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricDate}>
                  {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              size="small"
              style={{
                ...GeneratedTagsCardStyles.metricCard,
                border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, transactions, colorPicker)}`,
                backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                  date,
                  transactions,
                  colorPicker
                ),
              }}
              bodyStyle={GeneratedTagsCardStyles.metricCardBody}
            >
              <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                  Transactions
                </Text>
                <Text
                  style={{
                    ...GeneratedTagsCardStyles.metricValue,
                    color: GeneratedTagsCardStyles.getValueTextColor(
                      date,
                      transactions,
                      colorPicker
                    ),
                  }}
                >
                  {formatWholeNumberNaN(transactions)}
                </Text>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricDate}>
                  {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              size="small"
              style={{
                ...GeneratedTagsCardStyles.metricCard,
                border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, sessions, colorPicker)}`,
                backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                  date,
                  sessions,
                  colorPicker
                ),
              }}
              bodyStyle={GeneratedTagsCardStyles.metricCardBody}
            >
              <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                  Sessions
                </Text>
                <Text
                  style={{
                    ...GeneratedTagsCardStyles.metricValue,
                    color: GeneratedTagsCardStyles.getValueTextColor(date, sessions, colorPicker),
                  }}
                >
                  {formatWholeNumberNaN(sessions)}
                </Text>
                <Text type="secondary" style={GeneratedTagsCardStyles.metricDate}>
                  {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
                </Text>
              </Space>
            </Card>
          </Col>
          {hasBasketItems && (
            <Col xs={24} sm={12} md={12} lg={6}>
              <Card
                size="small"
                style={{
                  ...GeneratedTagsCardStyles.metricCard,
                  border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, basketItems, colorPicker)}`,
                  backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                    date,
                    basketItems,
                    colorPicker
                  ),
                }}
                bodyStyle={GeneratedTagsCardStyles.metricCardBody}
              >
                <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
                  <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                    Basket Items
                  </Text>
                  <Text
                    style={{
                      ...GeneratedTagsCardStyles.metricValue,
                      color: GeneratedTagsCardStyles.getValueTextColor(
                        date,
                        basketItems,
                        colorPicker
                      ),
                    }}
                  >
                    {formatWholeNumberNaN(basketItems)}
                  </Text>
                  <Text type="secondary" style={GeneratedTagsCardStyles.basketItemsDate}>
                    {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
                  </Text>
                </Space>
              </Card>
            </Col>
          )}
        </>
      );
    } else if (type === 'publisher') {
      return (
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            size="small"
            style={{
              ...GeneratedTagsCardStyles.metricCard,
              border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, pageViews, colorPicker)}`,
              backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                date,
                pageViews,
                colorPicker
              ),
            }}
            bodyStyle={GeneratedTagsCardStyles.metricCardBody}
          >
            <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
              <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                Page Views
              </Text>
              <Text
                style={{
                  ...GeneratedTagsCardStyles.metricValue,
                  color: GeneratedTagsCardStyles.getValueTextColor(date, pageViews, colorPicker),
                }}
              >
                {formatWholeNumberNaN(pageViews)}
              </Text>
              <Text type="secondary" style={GeneratedTagsCardStyles.basketItemsDate}>
                {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
              </Text>
            </Space>
          </Card>
        </Col>
      );
    } else if (type === 'impression') {
      return (
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            size="small"
            style={{
              ...GeneratedTagsCardStyles.metricCard,
              border: `2px solid ${GeneratedTagsCardStyles.getCardBorderColor(date, impressions, colorPicker)}`,
              backgroundColor: GeneratedTagsCardStyles.getCardBackgroundColor(
                date,
                impressions,
                colorPicker
              ),
            }}
            bodyStyle={GeneratedTagsCardStyles.metricCardBody}
          >
            <Space direction="vertical" size={4} style={GeneratedTagsCardStyles.metricSpace}>
              <Text type="secondary" style={GeneratedTagsCardStyles.metricLabel}>
                Impressions
              </Text>
              <Text
                style={{
                  ...GeneratedTagsCardStyles.metricValue,
                  color: GeneratedTagsCardStyles.getValueTextColor(date, impressions, colorPicker),
                }}
              >
                {impressions}
              </Text>
              <Text type="secondary" style={GeneratedTagsCardStyles.basketItemsDate}>
                {date ? moment(date).format('MMM DD, HH:mm') : 'No Signal'}
              </Text>
            </Space>
          </Card>
        </Col>
      );
    }
  };

  const hasBasketItems = basketItems > 0;
  return (
    <Card
      title={<Text strong>GENERATED TAG</Text>}
      style={GeneratedTagsCardStyles.mainCard}
      headStyle={GeneratedTagsCardStyles.mainCardHead}
      extra={<GenTagInstructionsModal cart={cart} generatedTag={generatedTagValue} />}
    >
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary" style={GeneratedTagsCardStyles.delayNotice}>
                Results shown below may be delayed up to 1 hour
              </Text>
            </Col>
            <Col>
              <GeneratedImagePixelTagModal cart={cart} appId={appId} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={[16, 16]} style={GeneratedTagsCardStyles.resultsRow}>
            {tagCards()}
          </Row>
        </Col>

        <Col span={24}>
          <Divider style={GeneratedTagsCardStyles.codeDivider} />
          <Space direction="vertical" size={8} style={GeneratedTagsCardStyles.codeContainer}>
            <Text type="secondary" strong style={GeneratedTagsCardStyles.codeTitle}>
              GENERATED TAG CODE
            </Text>
            <Card
              style={GeneratedTagsCardStyles.codeCard}
              bodyStyle={GeneratedTagsCardStyles.codeCardBody}
            >
              {/* VSCode-like header bar */}
              <div style={GeneratedTagsCardStyles.codeHeader}>
                <div style={GeneratedTagsCardStyles.codeHeaderDots}>
                  <div style={GeneratedTagsCardStyles.codeHeaderDotRed}></div>
                  <div style={GeneratedTagsCardStyles.codeHeaderDotYellow}></div>
                  <div style={GeneratedTagsCardStyles.codeHeaderDotGreen}></div>
                </div>
                <Text style={GeneratedTagsCardStyles.codeHeaderTitle}>generated-tag.js</Text>
              </div>

              <div style={{ position: 'relative' }}>
                <CodeBlock
                  text={generatedTagValue}
                  language="javascript"
                  theme={GeneratedTagsCardStyles.codeBlockTheme}
                  showLineNumbers={true}
                  wrapLongLines={false}
                  customStyle={{
                    ...GeneratedTagsCardStyles.codeBlockCustom,
                    padding: '16px',
                  }}
                />
                <Tooltip title={isCopied ? 'Copied!' : 'Copy'}>
                  <Button
                    type="text"
                    icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopy}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      color: '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                </Tooltip>
              </div>
            </Card>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default GeneratedTagsCard;
